import React, { useEffect, useState } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useLocation, useHistory } from 'react-router';
import { Formik, Form } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { useData } from 'resift';
import usePrevious from '@sift/resift/usePrevious';
import createWidget from 'fetches/homepageWidgets/createWidget';
import getWidgets from 'fetches/homepageWidgets/getWidgets';
import TextField from '@material-ui/core/TextField';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import PageFormToolbar from 'components/Page/newComponents/PageFormToolbar';
import PageFormErrorMessage from 'components/Page/newComponents/PageFormErrorMessage';
import SaveButton from 'components/Page/SaveButton';
import CancelButton from 'components/Page/CancelButton';
import WidgetTypeCard from 'pages/Homepage/components/WidgetTypeCard/WidgetTypeCard';
import CharCount from 'atoms/CharCount';
import { Widget, WidgetType } from 'pages/Homepage/typings';
import { HOME_URL, WIDGETS_URL } from 'components/Router/consts';
import { MAX_CHAR_COUNT_NAME } from 'pages/Homepage/helpers/consts';

function WidgetSetName() {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const widgets: Array<Widget> = useSelector(state => _get(state, ['homepageWidgets', 'widgets']));
  const widgetNames = !widgets.length ? [] : widgets.map(w => w.name);
  const widgetTypes: Array<WidgetType> = useSelector(state =>
    _get(state, ['homepageWidgets', 'widgetTypes']),
  );
  const newWidget = useData(createWidget);
  const prevNewWidget = usePrevious(newWidget);
  const [userHasTouchedTheForm, setUserHasTouchedTheForm] = useState(false);

  useEffect(() => {
    if (newWidget === prevNewWidget) {
      return;
    }

    history.push(`${HOME_URL}${WIDGETS_URL}/${newWidget.id}`);
  }, [newWidget]);

  if (
    location.pathname === `${HOME_URL}${WIDGETS_URL}/create/types` ||
    location.pathname === `${HOME_URL}${WIDGETS_URL}/create/types/`
  ) {
    return null;
  }

  const widgetTypeId = location.pathname
    .split('/')
    .filter(Boolean)
    .pop() as string;

  if (!widgetTypes.map(wt => wt.id).includes(widgetTypeId)) {
    return null;
  }

  const handleSave = async (name: string) => {
    await dispatch(
      createWidget({
        name,
        widgetTypeId,
      }),
    );
    dispatch(getWidgets(true));
  };

  const handleCancel = () => {
    history.push(`${HOME_URL}${WIDGETS_URL}/create/types`);
  };

  const handleValidate = (values: any) => {
    let errors: any = {};
    const name = _get(values, 'name');

    if (!name.length) {
      errors.name = 'Name is required';
    }

    if (widgetNames.includes(name)) {
      errors.name = 'This name already exists, please use a different name';
    }

    if (name.length > MAX_CHAR_COUNT_NAME) {
      errors.name = `Name cannot be more than ${MAX_CHAR_COUNT_NAME} characters`;
    }

    return errors;
  };

  return (
    <Formik initialValues={{ name: '' }} onSubmit={() => {}} validate={handleValidate}>
      {props => {
        const { values, setFieldValue, errors } = props;
        const widgetName = _get(values, 'name', '');

        return (
          <Form className={classes.root}>
            <Card className={classes.card}>
              <CardContent>
                <div className={classes.row}>
                  <Typography variant="h5">Widget Name</Typography>
                  <TextField
                    id="widget-name"
                    name="widget-name"
                    fullWidth
                    error={userHasTouchedTheForm && !_isEmpty(errors)}
                    value={widgetName}
                    onChange={e => {
                      if (!userHasTouchedTheForm) {
                        setUserHasTouchedTheForm(true);
                      }

                      setFieldValue('name', e.target.value);
                    }}
                    InputLabelProps={{
                      classes: {
                        root: classes.inputLabel,
                        focused: classes.cssFocused,
                      },
                    }}
                    inputProps={{
                      maxLength: MAX_CHAR_COUNT_NAME,
                      'aria-label': 'widget name',
                    }}
                    helperText={errors.name && errors.name}
                  />
                  <CharCount count={widgetName.length} maxCount={MAX_CHAR_COUNT_NAME} />
                </div>
              </CardContent>
            </Card>

            <WidgetTypeCard itemName={widgetTypeId} />

            <PageFormToolbar dirty preContent={<PageFormErrorMessage errors={errors} />}>
              <CancelButton onClick={handleCancel} />
              <SaveButton
                label="Save and Next"
                onClick={() => handleSave(widgetName)}
                disabled={!_isEmpty(errors) || !widgetName.length}
              />
            </PageFormToolbar>
          </Form>
        );
      }}
    </Formik>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  row: {
    marginBottom: theme.spacing(3),
  },
  inputLabel: {
    '&$cssFocused': {
      color: theme.palette.secondary.main,
    },
  },
  cssFocused: {},
  card: {
    flexGrow: 1,
    maxWidth: 900,
    marginRight: theme.spacing(3),
  },
}));

export default WidgetSetName;
